import { useState, useEffect, useRef } from "react";

/**
 * WatchlistTickerModal
 *
 * Modal for adding/removing tickers from a specific watchlist.
 * Uses the same /search-tickers API as StockView for company search.
 *
 * Props:
 *   isOpen          – boolean controlling visibility
 *   onClose         – callback to close the modal
 *   watchlistName   – name of the watchlist being edited
 *   onTickersChange – callback() fired after any add/remove so parent can refresh
 */
export default function WatchlistTickerModal({
  isOpen,
  onClose,
  watchlistName,
  onTickersChange,
}) {
  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const API_URL =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");

  const getUserEmail = () => {
    let email = localStorage.getItem("userEmail");
    if (!email) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          if (payload.email) {
            email = payload.email;
            localStorage.setItem("userEmail", email);
          }
        } catch (e) {
          /* ignore */
        }
      }
    }
    return email;
  };

  // ── Fetch tickers whenever modal opens ─────────────────────────────────
  useEffect(() => {
    if (!isOpen || !watchlistName) return;
    fetchTickers();
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen, watchlistName]);

  // ── Close search dropdown on outside click ─────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchTickers = async () => {
    const email = getUserEmail();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/watchlists/tickers?email=${encodeURIComponent(email)}&wl_name=${encodeURIComponent(watchlistName)}`
      );
      if (!res.ok) throw new Error("Failed to fetch tickers");
      const data = await res.json();
      setTickers(data.tickers || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Search companies (same API as StockView) ──────────────────────────
  const searchCompanies = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/search-tickers?q=${encodeURIComponent(query)}`
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.companies || []);
        setShowDropdown(true);
      }
    } catch (err) {
      console.error("Error searching companies:", err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchCompanies(value), 300);
  };

  // ── Add ticker ─────────────────────────────────────────────────────────
  const [adding, setAdding] = useState(false);

  const handleAddTicker = async (ticker) => {
    const email = getUserEmail();
    if (!email || !ticker || adding) return;
    setAdding(true);
    setError(null);
    // Immediately hide dropdown to prevent double-click
    setShowDropdown(false);
    try {
      const res = await fetch(`${API_URL}/watchlists/add-ticker`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, wl_name: watchlistName, ticker }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Add failed");
      setTickers(data.tickers || []);
      setSearchQuery("");
      setSearchResults([]);
      onTickersChange?.();
      inputRef.current?.focus();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  // ── Remove ticker ──────────────────────────────────────────────────────
  const handleRemoveTicker = async (ticker) => {
    const email = getUserEmail();
    if (!email) return;
    setError(null);
    try {
      const res = await fetch(`${API_URL}/watchlists/remove-ticker`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, wl_name: watchlistName, ticker }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Remove failed");
      setTickers(data.tickers || []);
      onTickersChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Select from search dropdown ────────────────────────────────────────
  const handleSelectCompany = (company) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'search', {
        search_term: company.ticker || company.name
      });
    }
    handleAddTicker(company.ticker);
  };

  if (!isOpen) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Modal */}
      <div
        className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-gray-100">
            Edit {watchlistName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none transition-colors"
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Search — top */}
        <div className="px-5 py-4 border-b border-gray-700 bg-gray-800/80">
          <label className="block text-xs text-gray-400 mb-1.5 font-medium">
            Search Company
          </label>
          <div className="relative" ref={dropdownRef}>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="e.g. Apple or AAPL"
              className="w-full bg-gray-700 text-gray-100 text-sm border border-gray-600 rounded-lg px-3 py-2 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-colors"
            />
            {searchLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                …
              </div>
            )}

            {/* Search results dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-gray-700 border border-gray-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                {searchResults.map((company) => {
                  const alreadyAdded = tickers.some(
                    (t) => t.ticker === company.ticker
                  );
                  return (
                    <div
                      key={company.ticker}
                      onClick={() =>
                        !alreadyAdded && handleSelectCompany(company)
                      }
                      className={`px-3 py-2 text-sm transition-colors flex items-center justify-between ${alreadyAdded
                          ? "text-gray-500 cursor-default"
                          : "text-gray-200 hover:bg-gray-600 cursor-pointer"
                        }`}
                    >
                      <div className="truncate">
                        <span className="font-medium">{company.ticker}</span>
                        <span className="text-gray-400 ml-2 text-xs">
                          {company.name}
                        </span>
                      </div>
                      {alreadyAdded && (
                        <span className="text-[10px] text-gray-500 ml-2 flex-shrink-0">
                          Added
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="text-[10px] text-gray-500 mt-1.5">
            {tickers.length}/10 tickers
          </div>
        </div>

        {/* Ticker list */}
        <div className="px-5 py-4 max-h-[50vh] overflow-y-auto">
          {/* Error banner */}
          {error && (
            <div className="mb-3 text-sm text-red-400 bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-gray-400 text-sm py-6 text-center">
              Loading…
            </div>
          ) : tickers.length === 0 ? (
            <div className="text-gray-500 text-sm py-6 text-center">
              No tickers yet — search above to add
            </div>
          ) : (
            <ul className="divide-y divide-gray-700/50">
              {tickers.map((t) => (
                <li
                  key={t.ticker}
                  className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-700/50 transition-colors"
                >
                  <span className="text-gray-200 text-sm font-medium">
                    {t.ticker}
                  </span>
                  <button
                    onClick={() => handleRemoveTicker(t.ticker)}
                    className="flex-shrink-0 ml-2 w-5 h-5 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center transition-colors shadow-sm"
                    title={`Remove ${t.ticker}`}
                  >
                    <span className="text-white text-xs font-bold leading-none">
                      −
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
